/**
 * 頁內載入指示（不蓋畫面）。
 *
 * 換頁時的資料抓取請用這支，別呼叫全域的 useUtilsStore.setLoading —— 那支會拉起
 * <Loading /> 全螢幕遮罩（.wrapper-loading，z-index 1000000），每次切頁都把畫面整片蓋掉。
 * 全域遮罩只保留給「必須擋住操作」的動作：LIFF 登入、註冊送出、下單、付款。
 *
 * - PageLoadingBar：頁面已有內容、只是背景更新 → 頂部細進度條。
 * - PageLoading：內容區還是空的 → 置中小轉圈。
 */

const KEYFRAMES = `
@keyframes pageLoadSlide {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(350%); }
}
@keyframes pageLoadSpin {
  to { transform: rotate(360deg); }
}
@media (prefers-reduced-motion: reduce) {
  .page-load-bar-inner { animation: none; width: 100%; opacity: 0.5; }
  .page-load-spinner { animation: none; border-right-color: rgba(59, 130, 246, 0.45); }
}
`;

/** 頂部細進度條：sticky 在捲動容器頂端，不佔版面、不擋點擊。 */
export const PageLoadingBar = ({ visible = true }: { visible?: boolean }) => {
  if (!visible) return null;
  return (
    <div
      aria-hidden
      style={{
        position: "sticky",
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        zIndex: 50,
        overflow: "hidden",
        pointerEvents: "none",
        /* 不畫軌道。原本鋪一條 rgba(59,130,246,0.12) 的底，在淺色頁面上會讀成
           一條橫貫全寬的分隔線——它出現在標題與內容之間，看起來像版面的一部分
           而不是「正在載入」。只留滑動的那一段，載入結束就什麼都不剩。 */
      }}>
      <style>{KEYFRAMES}</style>
      <div
        className="page-load-bar-inner"
        style={{
          height: "100%",
          width: "38%",
          background: "linear-gradient(90deg, transparent, rgba(59,130,246,0.75), transparent)",
          animation: "pageLoadSlide 1.1s ease-in-out infinite",
        }}
      />
    </div>
  );
};

/** 內容區置中轉圈：頁面還沒有東西可顯示時用，取代整頁遮罩。 */
export const PageLoading = ({ label = "載入中", minHeight = 160 }: { label?: string | null; minHeight?: number }) => (
  <div
    role="status"
    aria-live="polite"
    aria-label={label || "載入中"}
    style={{
      width: "100%",
      minHeight,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
    }}>
    <style>{KEYFRAMES}</style>
    <div
      className="page-load-spinner"
      aria-hidden
      style={{
        width: 26,
        height: 26,
        borderRadius: "50%",
        border: "2.5px solid rgba(59, 130, 246, 0.22)",
        borderTopColor: "#3B82F6",
        animation: "pageLoadSpin 0.7s linear infinite",
      }}
    />
    {label ? <div style={{ color: "#64748b", fontSize: 13, fontWeight: 600, letterSpacing: "0.04em" }}>{label}</div> : null}
  </div>
);

export default PageLoading;
